import React, { useEffect, useRef, useState } from 'react';
import { Eye, Mail, Pill, UserMinus, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

type MedcallListScope = 'all' | 'my';

type MedcallDashboardCidLinkProps = {
  cid?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  participantId: number;
};

type MedcallMedicationCountPopoverProps = {
  medicationCount?: number | null;
  medicationNames?: string[] | null;
  onViewAll: () => void;
  participantId: number;
};

type MedcallClientRowActionsProps = {
  canWrite: boolean;
  canManageMedication: boolean;
  isOnMyClients: boolean;
  onAddMedication: () => void;
  onAddToMyClients: () => void;
  onOpen: () => void;
  onRemoveFromMyClients: () => void;
  onSendMessage: () => void;
  participantId: number;
  pending: boolean;
  scope: MedcallListScope;
};

type RowActionProps = {
  children: React.ReactNode;
  disabled?: boolean;
  id: string;
  label: string;
  onClick: () => void;
};

const normalizeLabel = (value: unknown) => `${value || ''}`.trim();

const RowAction = ({
  children,
  disabled = false,
  id,
  label,
  onClick,
}: RowActionProps) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          aria-label={label}
          className="h-8 w-8 p-0"
          disabled={disabled}
          id={id}
          onClick={onClick}
          size="sm"
          title={label}
          type="button"
          variant="outline"
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{label}</TooltipContent>
    </Tooltip>
  );
};

export const MedcallDashboardCidLink = ({
  cid,
  firstname,
  lastname,
  participantId,
}: MedcallDashboardCidLinkProps) => {
  const navigate = useNavigate();
  const cidLabel = normalizeLabel(cid);
  if (!cidLabel) {
    return null;
  }

  const participantName = [firstname, lastname]
    .map(normalizeLabel)
    .filter(Boolean)
    .join(' ');

  return <Button id={`medcall-workspace-client-${participantId}-cid`} variant="link" className="h-8 p-0" onClick={() => navigate('/search', { state: { searchQuery: participantName } })}>{cidLabel}</Button>;

};

export const MedcallMedicationCountPopover = ({
  medicationCount,
  medicationNames,
  onViewAll,
  participantId,
}: MedcallMedicationCountPopoverProps) => {
  const count = Math.max(0, Number(medicationCount || 0));
  const label = count > 1 ? `${count} medications` : `${count} medication`;
  const names = (Array.isArray(medicationNames) ? medicationNames : [])
    .map(normalizeLabel)
    .filter(Boolean)
    .slice(0, 5);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const cancelClose = () => {
    if (closeTimer.current === null) {
      return;
    }

    window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      setOpen(false);
      closeTimer.current = null;
    }, 100);
  };

  useEffect(() => {
    return cancelClose;
  }, []);

  if (count < 1) {
    return <span className="text-xs">{label}</span>;
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <button
          aria-label={`${label}. Show latest medications`}
          className="text-xs text-[var(--azure-primary)]"
          id={`medcall-client-${participantId}-medications`}
          onBlur={scheduleClose}
          onFocus={() => {
            cancelClose();
            setOpen(true);
          }}
          onMouseEnter={() => {
            cancelClose();
            setOpen(true);
          }}
          onMouseLeave={scheduleClose}
          type="button"
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 space-y-2 p-4"
        onBlur={scheduleClose}
        onFocus={cancelClose}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        side="bottom"
      >
        <div className="text-xs font-semibold text-[var(--neutral-gray-100)]">LATEST MEDICATIONS</div>
        {names.map((name, index) => (
          <div
            key={`${participantId}-${name}-${index}`}
            className="text-sm"
          >
            {name}
          </div>
        ))}
        <button
          className="text-xs text-[var(--azure-primary)]"
          id={`medcall-client-${participantId}-view-all-medications`}
          onClick={() => {
            setOpen(false);
            onViewAll();
          }}
          type="button"
        >
          View All
        </button>
      </PopoverContent>
    </Popover>
  );
};

export const MedcallClientRowActions = ({
  canWrite,
  canManageMedication,
  isOnMyClients,
  onAddMedication,
  onAddToMyClients,
  onOpen,
  onRemoveFromMyClients,
  onSendMessage,
  participantId,
  pending,
  scope,
}: MedcallClientRowActionsProps) => {
  return (
    <TooltipProvider delayDuration={0}>
      <div
        className="flex items-center justify-end gap-2"
        data-testid="medcall-client-row-actions"
      >
        <RowAction
          id={`medcall-client-${participantId}-view`}
          label="View CLient"
          onClick={onOpen}
        >
          <Eye aria-hidden="true" />
        </RowAction>

        {scope === 'all' ? (
          <RowAction
            disabled={!canWrite || isOnMyClients || pending}
            id={`medcall-client-${participantId}-add-to-my-clients`}
            label="Add To My Clients"
            onClick={onAddToMyClients}
          >
            <UserPlus aria-hidden="true" />
          </RowAction>
        ) : null}

        {scope === 'my' && canManageMedication ? (
          <RowAction
            id={`medcall-client-${participantId}-add-medication`}
            label="Add Medication"
            onClick={onAddMedication}
          >
            <Pill aria-hidden="true" />
          </RowAction>
        ) : null}

        {scope === 'my' && isOnMyClients ? (
          <RowAction
            disabled={!canWrite || pending}
            id={`medcall-client-${participantId}-remove-from-my-clients`}
            label="Remove From My Clients"
            onClick={onRemoveFromMyClients}
          >
            <UserMinus aria-hidden="true" />
          </RowAction>
        ) : null}

        {scope === 'my' ? (
          <RowAction
            id={`medcall-client-${participantId}-send-message`}
            label="Send Message"
            onClick={onSendMessage}
          >
            <Mail aria-hidden="true" />
          </RowAction>
        ) : null}
      </div>
    </TooltipProvider>
  );
};
